import { create } from 'zustand';
import type { ReportFilter } from '@/services/PagesServices/CustomReportBuilderService';
import type { ActiveView, ChartType, AggregateFunction, SortOrder } from '../constants';

interface ReportBuilderState {
  selectedDataSource: string;
  selectedFields: string[];
  filters: ReportFilter[];
  sortBy: string;
  sortOrder: SortOrder;
  currentPage: number;
  pageSize: number;

  activeView: ActiveView;
  chartType: ChartType;
  chartGroupByField: string;
  chartAggregateField: string;
  chartAggregateFunction: AggregateFunction;

  reportName: string;
  aiPrompt: string;

  expandedSections: {
    dataSource: boolean;
    fields: boolean;
    filters: boolean;
    sortGroup: boolean;
    visualization: boolean;
  };
}

interface ReportBuilderActions {
  setSelectedDataSource: (source: string) => void;
  setSelectedFields: (fields: string[]) => void;
  toggleField: (field: string) => void;
  addFilter: (filter: ReportFilter) => void;
  removeFilter: (index: number) => void;
  updateFilter: (index: number, filter: ReportFilter) => void;
  clearFilters: () => void;
  setSortBy: (field: string) => void;
  setSortOrder: (order: SortOrder) => void;
  setCurrentPage: (page: number) => void;
  setPageSize: (size: number) => void;
  setActiveView: (view: ActiveView) => void;
  setChartType: (type: ChartType) => void;
  setChartGroupByField: (field: string) => void;
  setChartAggregateField: (field: string) => void;
  setChartAggregateFunction: (fn: AggregateFunction) => void;
  setReportName: (name: string) => void;
  setAiPrompt: (prompt: string) => void;
  toggleSection: (section: keyof ReportBuilderState['expandedSections']) => void;
  resetBuilder: () => void;
}

const INITIAL_STATE: ReportBuilderState = {
  selectedDataSource: '',
  selectedFields: [],
  filters: [],
  sortBy: '',
  sortOrder: 'asc',
  currentPage: 1,
  pageSize: 25,
  activeView: 'table',
  chartType: 'bar',
  chartGroupByField: '',
  chartAggregateField: '',
  chartAggregateFunction: 'count',
  reportName: '',
  aiPrompt: '',
  expandedSections: {
    dataSource: true,
    fields: true,
    filters: false,
    sortGroup: false,
    visualization: false,
  },
};

export const useReportBuilderState = create<ReportBuilderState & ReportBuilderActions>((set, get) => ({
  ...INITIAL_STATE,

  setSelectedDataSource: (source) => set({
    selectedDataSource: source,
    selectedFields: [],
    filters: [],
    sortBy: '',
    sortOrder: 'asc',
    currentPage: 1,
  }),

  setSelectedFields: (fields) => set({ selectedFields: fields }),

  toggleField: (field) => {
    const { selectedFields } = get();
    set({
      selectedFields: selectedFields.includes(field)
        ? selectedFields.filter(f => f !== field)
        : [...selectedFields, field],
    });
  },

  addFilter: (filter) => set({ filters: [...get().filters, filter] }),

  removeFilter: (index) => set({
    filters: get().filters.filter((_, i) => i !== index),
  }),

  updateFilter: (index, filter) => {
    const filters = [...get().filters];
    filters[index] = filter;
    set({ filters });
  },

  clearFilters: () => set({ filters: [] }),
  setSortBy: (field) => set({ sortBy: field }),
  setSortOrder: (order) => set({ sortOrder: order }),
  setCurrentPage: (page) => set({ currentPage: page }),
  setPageSize: (size) => set({ pageSize: size, currentPage: 1 }),
  setActiveView: (view) => set({ activeView: view }),
  setChartType: (type) => set({ chartType: type }),
  setChartGroupByField: (field) => set({ chartGroupByField: field }),
  setChartAggregateField: (field) => set({ chartAggregateField: field }),
  setChartAggregateFunction: (fn) => set({ chartAggregateFunction: fn }),
  setReportName: (name) => set({ reportName: name }),
  setAiPrompt: (prompt) => set({ aiPrompt: prompt }),

  toggleSection: (section) => set({
    expandedSections: {
      ...get().expandedSections,
      [section]: !get().expandedSections[section],
    },
  }),

  resetBuilder: () => set(INITIAL_STATE),
}));
